<template>
  <input
    ref="inputRef"
    :value="localValue"
    type="email"
    inputmode="email"
    :autocomplete="autocomplete"
    :class="$attrs.class"
    :placeholder="placeholder"
    :disabled="disabled"
    :required="required"
    :name="name"
    maxlength="254"
    spellcheck="false"
    autocapitalize="off"
    autocorrect="off"
    @keydown="onKeydown"
    @beforeinput="onBeforeInput"
    @input="onInput"
    @paste="onPaste"
    @blur="onBlur"
  />
</template>

<script setup>
import { ref, watch } from 'vue'
import { isEmailCharAllowed, normalizeEmail, sanitizeEmailInput } from '@/utils/emailInput'

defineOptions({ inheritAttrs: false })

const props = defineProps({
  modelValue: { type: String, default: '' },
  disabled: { type: Boolean, default: false },
  placeholder: { type: String, default: 'name@example.com' },
  required: { type: Boolean, default: false },
  autocomplete: { type: String, default: 'email' },
  name: { type: String, default: undefined },
})

const emit = defineEmits(['update:modelValue'])

const inputRef = ref(null)
const localValue = ref('')

function syncFromModel() {
  localValue.value = props.modelValue ? sanitizeEmailInput(props.modelValue) : ''
}

watch(() => props.modelValue, syncFromModel, { immediate: true })

function applyValue(next, caretPos) {
  const sanitized = sanitizeEmailInput(next)
  localValue.value = sanitized
  emit('update:modelValue', sanitized)

  const el = inputRef.value
  if (!el) return
  const pos = Math.min(caretPos ?? sanitized.length, sanitized.length)
  if (el.value !== sanitized) el.value = sanitized
  try {
    el.setSelectionRange(pos, pos)
  } catch {
    /* ignore */
  }
}

function onInput(event) {
  const el = event.target
  const raw = String(el.value ?? '')
  const sel = el.selectionStart ?? raw.length
  applyValue(raw, sel)
}

function onPaste(event) {
  event.preventDefault()
  const text = event.clipboardData?.getData('text') ?? ''
  const el = inputRef.value
  if (!el) return
  const raw = String(el.value ?? '')
  const start = el.selectionStart ?? raw.length
  const end = el.selectionEnd ?? start
  const merged = raw.slice(0, start) + text + raw.slice(end)
  applyValue(merged, start + sanitizeEmailInput(text).length)
}

const CONTROL_KEYS = new Set([
  'Backspace',
  'Delete',
  'Tab',
  'Escape',
  'Enter',
  'ArrowLeft',
  'ArrowRight',
  'ArrowUp',
  'ArrowDown',
  'Home',
  'End',
])

function onKeydown(event) {
  if (props.disabled) return
  if (event.ctrlKey || event.metaKey || event.altKey) return
  if (CONTROL_KEYS.has(event.key)) return
  if (event.key.length === 1 && !isEmailCharAllowed(event.key)) {
    event.preventDefault()
  }
}

function onBeforeInput(event) {
  if (props.disabled) return
  const type = event.inputType || ''
  if (type.startsWith('delete')) return

  const data = event.data
  if (data == null) return
  if ([...data].some((ch) => !isEmailCharAllowed(ch) && ch !== ' ')) {
    event.preventDefault()
    return
  }
  if (data.includes(' ')) {
    event.preventDefault()
  }
}

function onBlur() {
  const normalized = normalizeEmail(localValue.value)
  if (normalized !== localValue.value) {
    localValue.value = normalized
    emit('update:modelValue', normalized)
  }
}
</script>
